import {
  AsyncSelect,
  Button,
  DateSelect,
  Dropdown as CommonDropdown,
  DropdownButton,
  DropdownMenu,
  DropdownMenuItem,
  Heading,
  Input,
  MultiSelect,
  MultiSelectOptionType,
  Select,
  Stack,
} from '@sixfold/common-ui';
import React, { useCallback, useMemo, useState } from 'react';

import { CompaniesDropdownContainer } from '../../company/containers/companies_dropdown_container';
import {
  notEmpty,
  TourStatusFilter,
  TourDelayStatusFilter,
  TourTextSearchField,
  TourFilteringTransportMode,
} from '../../lib/graphql';
import { Platform } from '../../platform/entities';
import { comparePlatform } from '../../platform/utils';

export interface TourFiltersProps {
  filters: {
    platforms: Platform[];
    places?: { place_id: string; name: string }[];
    vehicle: { vehicle_id: string; license_plate_number: string | null } | null;
  };

  activeFilters: {
    status?: string;
    delay?: string;
    shipper_id?: string;
    carrier_id?: string;
    platform_id?: string;
    platform_branch_id?: string;
    vehicle_id?: string;
    start_time_start?: string;
    start_time_end?: string;
    place_id?: string;
    search?: string;
    search_field?: string;
    transport_mode?: string;
  };
  onFilterToggle: (filter: { name: string; value: string | undefined }) => void;
  onClearFilters: () => void;
}

function toLocalDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function parseLocalDate(dateString: string): Date {
  return new Date(`${dateString}T00:00:00`);
}

export const TourFilters: React.FunctionComponent<TourFiltersProps> = (props) => {
  const { filters, activeFilters, onFilterToggle, onClearFilters } = props;
  const [customPlatformBranches, setCustomPlatformBranches] = useState(['VISIBILITY_HUB_INDEPENDENT']);

  const sortedPlatforms = useMemo(() => filters.platforms.filter(notEmpty).sort(comparePlatform), [filters.platforms]);
  const sortedPlaces =
    filters.places !== undefined ? [...filters.places].sort((a, b) => a.name.localeCompare(b.name)) : [];

  const statusOptions = [
    { value: TourStatusFilter.UNALLOCATED.toLowerCase(), label: 'Unallocated' },
    { value: TourStatusFilter.ALLOCATED.toLowerCase(), label: 'Allocated' },
    { value: TourStatusFilter.WAITING.toLowerCase(), label: 'Waiting' },
    { value: TourStatusFilter.WORKING.toLowerCase(), label: 'Working' },
    { value: TourStatusFilter.COMPLETED.toLowerCase(), label: 'Completed' },
    { value: TourStatusFilter.FAILED.toLowerCase(), label: 'Failed' },
  ];

  const delayOptions = [
    { value: TourDelayStatusFilter.ON_TIME.toLowerCase(), label: 'On time' },
    { value: TourDelayStatusFilter.DELAYED.toLowerCase(), label: 'Delay on pickup/delivery' },
    { value: TourDelayStatusFilter.DELAYED_PICKUP.toLowerCase(), label: 'Delay on pickup' },
    { value: TourDelayStatusFilter.DELAYED_DELIVERY.toLowerCase(), label: 'Delay on delivery' },
  ];

  const transportModeOptions: MultiSelectOptionType[] = [
    { value: TourFilteringTransportMode.Road.toLowerCase(), label: 'Road' },
    { value: TourFilteringTransportMode.Ocean.toLowerCase(), label: 'Ocean' },
    { value: TourFilteringTransportMode.Air.toLowerCase(), label: 'Air' },
    { value: TourFilteringTransportMode.Inland.toLowerCase(), label: 'Inland' },
  ];

  const platformOptions = sortedPlatforms.map((platform) => ({
    value: platform.platform_id,
    label: platform.platform_name,
  }));

  const allPlatformBranchIds = useMemo(
    () => [
      ...Array.from(new Set(sortedPlatforms.flatMap((platform) => platform.platform_branches))).filter(notEmpty),
      ...customPlatformBranches,
    ],
    [sortedPlatforms, customPlatformBranches],
  );

  const platformBranchOptions = useMemo(
    () =>
      Array.from(new Set(allPlatformBranchIds)).map((platformBranchId) => ({
        value: platformBranchId,
        label: platformBranchId,
      })),
    [allPlatformBranchIds],
  );

  const placeOptions = sortedPlaces?.map((place) => ({
    value: place.place_id,
    label: place.name,
  }));

  const searchOptions = [
    { text: 'All', value: TourTextSearchField.ALL_EXTENDED },
    { text: 'Tour ID', value: TourTextSearchField.TOUR_ID },
    { text: 'Delivery No.', value: TourTextSearchField.DELIVERY_NUMBER },
    { text: 'Order No.', value: TourTextSearchField.DELIVERY_ORDER_NUMBER },
    { text: 'Delivery PO No.', value: TourTextSearchField.DELIVERY_PURCHASE_ORDER_NUMBER },
    { text: 'Shipper transport No.', value: TourTextSearchField.SHIPPER_TRANSPORT_NUMBER },
    { text: 'Platform transport No.', value: TourTextSearchField.PLATFORM_TRANSPORT_NUMBER },
    { text: 'Bill of Lading', value: TourTextSearchField.BILL_OF_LADING },
    { text: 'Stop address', value: TourTextSearchField.STOP_ADDRESS },
    { text: 'Stop name', value: TourTextSearchField.STOP_NAME },
  ];

  const currentSearchFieldValue =
    activeFilters.search_field === undefined
      ? TourTextSearchField.ALL_EXTENDED
      : activeFilters.search_field.toUpperCase();
  const selectedSearchFieldLabel = searchOptions.find((o) => o.value === currentSearchFieldValue)?.text ?? 'All';

  const fetchPlatformBranchOptions = useCallback(
    async (input: string) => {
      const filtered = platformBranchOptions.filter((o) => o.label.toLowerCase().includes(input.toLowerCase()));
      if (input.length > 0 && !platformBranchOptions.some((o) => o.value.toLowerCase() === input.toLowerCase())) {
        filtered.push({ value: input, label: `Add "${input}"` });
      }
      return filtered;
    },
    [platformBranchOptions],
  );

  const filterItemStyle = { width: '300px' } as const;

  return (
    <Stack direction="column" gap="medium" className="tourFilters">
      <Heading size="large">
        {activeFilters.vehicle_id !== undefined && filters.vehicle !== null ? (
          <Stack direction="row" alignItems="center">
            <span>Tours with</span>
            <span
              style={{
                margin: '0 8px 0 4px',
                whiteSpace: 'nowrap',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
              }}>
              <span>🚚</span>
              {filters.vehicle.license_plate_number ?? '-'}
              <button
                type="button"
                onClick={() => onFilterToggle({ name: 'vehicle_id', value: undefined })}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0 2px',
                  fontSize: '14px',
                  lineHeight: 1,
                }}
                aria-label="Remove vehicle filter">
                ×
              </button>
            </span>
          </Stack>
        ) : (
          'Tours'
        )}
      </Heading>
      <Stack gap="small">
        <div style={{ display: 'flex', gap: '0.5em', alignItems: 'center' }}>
          <div style={filterItemStyle}>
            <Select
              isFilterable
              isClearable
              label="Status"
              labelPosition="hidden"
              placeholder="All statuses"
              options={statusOptions}
              value={statusOptions.find((o) => o.value === activeFilters.status) ?? null}
              onChange={(option) => {
                onFilterToggle({ name: 'status', value: option?.value });
              }}
            />
          </div>
          <div style={filterItemStyle}>
            <Select
              isFilterable
              isClearable
              label="Delay"
              labelPosition="hidden"
              placeholder="All delays"
              options={delayOptions}
              value={delayOptions.find((o) => o.value === activeFilters.delay) ?? null}
              onChange={(option) => {
                onFilterToggle({ name: 'delay', value: option?.value });
              }}
            />
          </div>
          <div style={filterItemStyle}>
            <CompaniesDropdownContainer
              inputProps={{
                placeholder: 'All shippers',
              }}
              selectedCompany={
                activeFilters.shipper_id !== undefined
                  ? {
                      company_id: activeFilters.shipper_id,
                    }
                  : null
              }
              onSelectCompany={(selectedCompany) =>
                onFilterToggle({
                  name: 'shipper_id',
                  value: selectedCompany === null ? undefined : selectedCompany.company_id,
                })
              }
              companyType={{
                is_shipper: true,
              }}
            />
          </div>
          <div style={filterItemStyle}>
            <CompaniesDropdownContainer
              inputProps={{
                placeholder: 'All carriers',
              }}
              selectedCompany={
                activeFilters.carrier_id !== undefined
                  ? {
                      company_id: activeFilters.carrier_id,
                    }
                  : null
              }
              onSelectCompany={(selectedCompany) =>
                onFilterToggle({
                  name: 'carrier_id',
                  value: selectedCompany === null ? undefined : selectedCompany.company_id,
                })
              }
              companyType={{
                is_carrier: true,
              }}
            />
          </div>
          <div style={filterItemStyle}>
            <MultiSelect
              placeholder="All transport modes"
              options={transportModeOptions}
              value={(activeFilters.transport_mode?.split(',') ?? []).flatMap((v) => {
                const opt = transportModeOptions.find((o) => o.value === v);
                return opt ? [opt] : [];
              })}
              onChange={(selectedOptions) => {
                const values = selectedOptions.map((o) => o.value);
                onFilterToggle({
                  name: 'transport_mode',
                  value: values.length === 0 ? undefined : values.join(','),
                });
              }}
            />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5em', alignItems: 'center' }}>
          <div style={filterItemStyle}>
            <DateSelect
              label="Started after"
              labelPosition="hidden"
              placeholder="Started after"
              name="start_time_start"
              size="small"
              format="dd.MM.yyyy"
              value={
                activeFilters.start_time_start !== undefined ? parseLocalDate(activeFilters.start_time_start) : null
              }
              onChange={(date) => {
                onFilterToggle({
                  name: 'start_time_start',
                  value: date !== null ? toLocalDateString(date) : undefined,
                });
              }}
            />
          </div>
          <div style={filterItemStyle}>
            <DateSelect
              label="Started before"
              labelPosition="hidden"
              placeholder="Started before"
              name="start_time_end"
              size="small"
              format="dd.MM.yyyy"
              value={activeFilters.start_time_end !== undefined ? parseLocalDate(activeFilters.start_time_end) : null}
              onChange={(date) => {
                onFilterToggle({
                  name: 'start_time_end',
                  value: date !== null ? toLocalDateString(date) : undefined,
                });
              }}
            />
          </div>
          <div style={filterItemStyle}>
            <Select
              isFilterable
              isClearable
              label="Platform"
              labelPosition="hidden"
              placeholder="All platforms"
              options={platformOptions}
              value={platformOptions.find((o) => o.value === activeFilters.platform_id) ?? null}
              onChange={(option) => {
                onFilterToggle({ name: 'platform_id', value: option?.value });
              }}
            />
          </div>
          <div style={filterItemStyle}>
            <AsyncSelect
              label="Platform branch"
              labelPosition="hidden"
              placeholder="All platform branches"
              isClearable
              defaultOptions={platformBranchOptions}
              fetchOptions={fetchPlatformBranchOptions}
              value={
                activeFilters.platform_branch_id !== undefined
                  ? { value: activeFilters.platform_branch_id, label: activeFilters.platform_branch_id }
                  : null
              }
              onChange={(option) => {
                const value = option?.value;
                if (value !== undefined && !customPlatformBranches.includes(value)) {
                  setCustomPlatformBranches([...customPlatformBranches, value]);
                }
                onFilterToggle({
                  name: 'platform_branch_id',
                  value: value === undefined ? undefined : value,
                });
              }}
            />
          </div>
          <div style={filterItemStyle}>
            <Select
              isFilterable
              isClearable
              label="Place"
              labelPosition="hidden"
              placeholder="All places"
              options={placeOptions}
              value={placeOptions.find((o) => o.value === activeFilters.place_id) ?? null}
              onChange={(option) => {
                onFilterToggle({ name: 'place_id', value: option?.value });
              }}
            />
          </div>
        </div>
      </Stack>
      <Stack direction="row" gap="small" alignItems="center" style={{ marginTop: '0.5em' }}>
        <CommonDropdown align="start">
          <DropdownButton kind="neutral" size="small">
            {selectedSearchFieldLabel}
          </DropdownButton>
          <DropdownMenu>
            {searchOptions.map((option) => (
              <DropdownMenuItem
                key={option.value}
                selected={currentSearchFieldValue === option.value}
                onClick={() => {
                  onFilterToggle({
                    name: 'search_field',
                    value: option.value === TourTextSearchField.ALL_EXTENDED ? undefined : option.value.toLowerCase(),
                  });
                }}>
                {option.text}
              </DropdownMenuItem>
            ))}
          </DropdownMenu>
        </CommonDropdown>
        <div style={{ width: '600px' }}>
          <Input
            name="search"
            label="Search"
            labelPosition="hidden"
            placeholder="Search (type >=3 characters)"
            size="small"
            value={activeFilters.search === undefined ? '' : activeFilters.search}
            onChange={({ value }) => {
              onFilterToggle({ name: 'search', value: value === '' ? undefined : value });
            }}
          />
        </div>
        {Object.keys(activeFilters).some((key) => activeFilters[key] !== undefined) ? (
          <Button onClick={onClearFilters} size="small">
            Clear filters
          </Button>
        ) : null}
      </Stack>
    </Stack>
  );
};
